import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { splitFullName, joinFullName } from "@/lib/name";

// Create / edit dialog for a single photographer record. Lifted out of
// Photographers.jsx so the page component stays a list view; the form's
// validation rules live next to the inputs they protect.
//
// UX (Feb 2026): a single "Full name" input replaces the old First/Last
// pair — matches the way clubs record names on paper rosters ("Danny
// Godfrey", "John George Doo", "S Green"). We auto-split into first/last
// on save (last word = surname, everything before it = first name) so
// the DB shape stays compatible with the rest of the app. Users can
// still override the on-screen label via the Display name field, and the
// underlying REST API is unchanged.
export function PhotographerDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    ...initial,
    full_name: joinFullName({
      first_name: initial.first_name || "",
      last_name: initial.last_name || "",
      suffix: "",
    }),
  }));
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial.id;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { first_name, last_name, suffix } = splitFullName(form.full_name);
      if (!last_name) {
        setErr("Please enter at least a last name.");
        setBusy(false);
        return;
      }
      // Display name defaults to the whole thing the user typed (with
      // suffix preserved). Explicit override still wins.
      const defaultDisplay = joinFullName({ first_name, last_name, suffix });
      const payload = {
        photographer_code: form.photographer_code,
        first_name,
        last_name,
        display_name: (form.display_name || "").trim() || defaultDisplay,
        membership_number: form.membership_number || null,
        email: form.email || null,
        tel_no: form.tel_no || null,
        active_status: !!form.active_status,
        enters_competitions: form.enters_competitions !== false,
      };
      if (isEdit) await api.patch(`/photographers/${initial.id}`, payload);
      else await api.post("/photographers", payload);
      toast.success(
        isEdit
          ? `Saved changes to ${payload.display_name}`
          : `Added ${payload.display_name} to the roster`
      );
      onSaved();
    } catch (ex) {
      const msg = formatApiError(ex.response?.data?.detail) || ex.message;
      setErr(msg);
      toast.error(`Couldn't save: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dialog-bg" onClick={onClose}>
      <form
        className="dialog-panel p-6"
        onClick={(e) => e.stopPropagation()}
        onSubmit={save}
        data-testid="photographer-dialog"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-semibold">
            {isEdit ? "Edit photographer" : "New photographer"}
          </h3>
          <button type="button" onClick={onClose} className="p-1">
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="form-row col-span-1">
            <label className="ccs-label">I.D. *</label>
            <input
              required
              className="ccs-input ccs-input-mono uppercase"
              value={form.photographer_code}
              onChange={(e) => setField("photographer_code", e.target.value.toUpperCase())}
              data-testid="photographer-code-input"
            />
          </div>
          <div className="form-row col-span-1">
            <label className="ccs-label">Membership #</label>
            <input
              className="ccs-input ccs-input-mono"
              value={form.membership_number || ""}
              onChange={(e) => setField("membership_number", e.target.value)}
            />
          </div>
          <div className="form-row col-span-2">
            <label className="ccs-label">Full name *</label>
            <input
              required
              className="ccs-input"
              value={form.full_name}
              onChange={(e) => setField("full_name", e.target.value)}
              placeholder="Danny Godfrey  ·  John George Doo  ·  S Green"
              data-testid="photographer-full-name"
              autoComplete="off"
            />
            <div className="text-xs muted-text mt-1">
              Type the whole name — we&rsquo;ll treat the last word as the surname
              (so &ldquo;John George Doo&rdquo; becomes first = John George, last = Doo).
              Honorific suffixes like LRPS / ARPS are recognised.
            </div>
          </div>
          <div className="form-row col-span-2">
            <label className="ccs-label">Display name</label>
            <input
              className="ccs-input"
              value={form.display_name || ""}
              onChange={(e) => setField("display_name", e.target.value)}
            />
          </div>
          <div className="form-row col-span-2">
            <label className="ccs-label">Email</label>
            <input
              type="email"
              className="ccs-input"
              value={form.email || ""}
              onChange={(e) => setField("email", e.target.value)}
            />
          </div>
          <div className="form-row col-span-2">
            <label className="ccs-label">Tel No.</label>
            <input
              type="tel"
              className="ccs-input ccs-input-mono"
              value={form.tel_no || ""}
              onChange={(e) => setField("tel_no", e.target.value)}
              placeholder="e.g. 07700 900123"
              data-testid="photographer-tel-input"
            />
          </div>
          <label className="flex items-center gap-2 col-span-2 mt-1 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.active_status}
              onChange={(e) => setField("active_status", e.target.checked)}
            />
            Active member
          </label>
          <label className="flex items-start gap-2 col-span-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.enters_competitions !== false}
              onChange={(e) => setField("enters_competitions", e.target.checked)}
              className="mt-1 accent-amber-400"
              data-testid="photographer-enters-competitions"
            />
            <div>
              <div>Enters competitions</div>
              <div className="text-xs muted-text">
                When OFF the &ldquo;Add all photographers&rdquo; button in Live Entry skips this member. You can still add them individually for one-off entries.
              </div>
            </div>
          </label>
        </div>

        {err && <div className="danger-text text-sm mt-3">{err}</div>}

        <div className="flex justify-end gap-3 mt-6">
          <button type="button" onClick={onClose} className="ccs-btn-ghost">
            Cancel
          </button>
          <button type="submit" disabled={busy} className="ccs-btn" data-testid="save-photographer">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
